// components/food-logger/FoodEntryForm.jsx
import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription } from "../ui/alert";
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// Unit conversion constants
const UNIT_CONVERSIONS = {
  g: 1,
  oz: 28.3495,
  cup: 236.588, // This is an average, as cup weight varies by food density
  serving: 1 // Use the food's serving size as the base
};

const FoodEntryForm = ({ food, onSave, onCancel }) => {
  const { user } = useAuth();
  const [servingSize, setServingSize] = useState(food.serving_size || 0);
  const [servingUnit, setServingUnit] = useState(food.serving_unit || 'g');
  const [mealType, setMealType] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nutritionValues, setNutritionValues] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });

  // Convert between different units
  const convertAmount = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return value;
    // Convert to grams first
    const grams = value * UNIT_CONVERSIONS[fromUnit];
    // Then convert to target unit
    return grams / UNIT_CONVERSIONS[toUnit];
  };

  // Calculate nutrition values based on serving size and unit
  const calculateNutrition = () => {
    // First convert the current serving size to grams
    const currentServingInGrams = convertAmount(servingSize, servingUnit, 'g');
    // Get the base serving size in grams
    const baseServingInGrams = convertAmount(food.serving_size, food.serving_unit, 'g');
    
    // Calculate the ratio
    const ratio = currentServingInGrams / baseServingInGrams;

    // Calculate nutrition values
    return {
      calories: (food.calories * ratio).toFixed(1),
      protein: (food.protein_g * ratio).toFixed(1),
      carbs: (food.carbs_g * ratio).toFixed(1),
      fat: (food.fat_g * ratio).toFixed(1)
    };
  };

  // Update nutrition values when serving size or unit changes
  useEffect(() => {
    const newNutritionValues = calculateNutrition();
    setNutritionValues(newNutritionValues);
  }, [servingSize, servingUnit]);

  // Handle serving unit change
  const handleUnitChange = (newUnit) => {
    // Convert current serving size to the new unit
    const newSize = convertAmount(servingSize, servingUnit, newUnit);
    setServingSize(parseFloat(newSize.toFixed(2)));
    setServingUnit(newUnit);
  };

  const handleSave = async () => {
    if (!mealType) {
      setError('Please select a meal type');
      return;
    }
  
    setSaving(true);
    setError(null);
  
    try {
      // First, ensure the food exists in our database
      const { data: existingFood } = await supabase
        .from('foods')
        .select('id')
        .eq('usda_fdc_id', food.usda_fdc_id)
        .single();
  
      let foodId;
      if (existingFood) {
        foodId = existingFood.id;
      } else {
        // Insert the new food
        const { data: newFood, error: foodError } = await supabase
          .from('foods')
          .insert([{
            ...food,
            calories: parseFloat(food.calories),
            protein_g: parseFloat(food.protein_g),
            carbs_g: parseFloat(food.carbs_g),
            fat_g: parseFloat(food.fat_g)
          }])
          .select('id')
          .single();
  
        if (foodError) throw foodError;
        foodId = newFood.id;
      }
  
      // Create meal log
      const { data: mealLog, error: mealError } = await supabase
        .from('meal_logs')
        .insert([{
          user_id: user.id,
          meal_type: mealType,
          meal_time: new Date().toISOString(),
          notes: notes
        }])
        .select('id')
        .single();
  
      if (mealError) throw mealError;
  
      // Convert the serving size to grams for consistency
      const servingSizeInGrams = convertAmount(servingSize, servingUnit, 'g');
      
      // Create food entry
      const { error: entryError } = await supabase
        .from('meal_food_entries')
        .insert([{
          meal_log_id: mealLog.id,
          food_id: foodId,
          serving_size: servingSizeInGrams,
          serving_unit: 'g', // Always store in grams for consistency
          notes: notes
        }]);
  
      if (entryError) throw entryError;
  
      onSave();
    } catch (error) {
      console.error('Error saving food entry:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{food.name}</CardTitle>
        {food.brand_name && <CardDescription>{food.brand_name}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label>Serving Size</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={servingSize}
              onChange={(e) => setServingSize(parseFloat(e.target.value) || 0)}
              className="w-24"
              min="0"
              step="0.1"
            />
            <Select value={servingUnit} onValueChange={handleUnitChange}>
              <SelectTrigger className="w-24">
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="g">grams</SelectItem>
                <SelectItem value="oz">ounces</SelectItem>
                <SelectItem value="cup">cups</SelectItem>
                <SelectItem value="serving">servings</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Meal Type</Label>
          <Select value={mealType} onValueChange={setMealType}>
            <SelectTrigger>
              <SelectValue placeholder="Select meal type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="breakfast">Breakfast</SelectItem>
              <SelectItem value="lunch">Lunch</SelectItem>
              <SelectItem value="dinner">Dinner</SelectItem>
              <SelectItem value="snack">Snack</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Notes (Optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this food..."
          />
        </div>

        <div className="pt-4 border-t">
          <h4 className="font-medium mb-2">Nutrition Info</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Calories</p>
              <p className="font-medium">{nutritionValues.calories} cal</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Protein</p>
              <p className="font-medium">{nutritionValues.protein}g</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Carbs</p>
              <p className="font-medium">{nutritionValues.carbs}g</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Fat</p>
              <p className="font-medium">{nutritionValues.fat}g</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Add Food"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FoodEntryForm;